/**
 * FinanceReceipts — full paginated table of every manual receipt across
 * all trips. Linked to from the Collections widget on Dashboard+ via the
 * "View all →" affordance (Phase 16.2).
 *
 * Backed by GET /api/finance/receipts which explodes trip.receipts arrays
 * into rows + decorates with trip metadata.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Receipt as ReceiptIcon, IndianRupee, TrendingUp } from 'lucide-react';
import ExportButton from '@/components/list/ExportButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtBalance = (n) => (n === null || n === undefined ? '—' : fmtINR(n));
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};

const METHODS = ['', 'Bank Transfer', 'UPI', 'Cash', 'Cheque', 'Card', 'Other'];

const FinanceReceipts = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ rows: 0, amount: 0, todayCashIn: 0 });
  const [filters, setFilters] = useState({
    start: '', end: '', method: '', traveller: '', trip_id: '',
  });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const fetchRows = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filters.start)     q.set('start', filters.start);
      if (filters.end)       q.set('end', filters.end);
      if (filters.method)    q.set('method', filters.method);
      if (filters.traveller) q.set('traveller', filters.traveller);
      if (filters.trip_id)   q.set('trip_id', filters.trip_id);
      q.set('skip', String(page * PAGE_SIZE));
      q.set('limit', String(PAGE_SIZE));
      const r = await axios.get(`${BACKEND_URL}/api/finance/receipts?${q.toString()}`, { withCredentials: true });
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || { rows: 0, amount: 0 });
    } catch {
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [page]);

  const onApplyFilters = () => {
    setPage(0);
    fetchRows();
  };

  const exportColumns = useMemo(() => ([
    { key: 'date',         label: 'Date',       format: fmtDate },
    { key: 'tripDisplayId', label: 'Trip ID' },
    { key: 'traveller',    label: 'Traveller' },
    { key: 'clientUCC',    label: 'UCC' },
    { key: 'destination',  label: 'Destination' },
    { key: 'amount',       label: 'Amount',     format: (v) => Number(v || 0) },
    { key: 'method',       label: 'Method' },
    { key: 'bankRef',      label: 'UTR / Ref' },
    { key: 'type',         label: 'Type' },
    { key: 'balanceAfter', label: 'Balance after', format: (v) => (v === null || v === undefined ? '' : Number(v)) },
    { key: 'source',       label: 'Source' },
    { key: 'note',         label: 'Note' },
  ]), []);

  return (
    <div className="space-y-3" data-testid="finance-receipts-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          {!embedded ? (
            <>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <Link to="/" className="hover:underline">Dashboard+</Link>
                <span className="mx-1 text-slate-300">/</span>
                <Link to="/accounting" className="hover:underline">Finance</Link>
                <span className="mx-1 text-slate-300">/</span>
                <span>Receipts</span>
              </p>
              <h1 className="text-2xl font-semibold text-slate-900 mt-0.5 flex items-center gap-2">
                <ReceiptIcon className="h-5 w-5 text-emerald-600" />
                Receipts
              </h1>
            </>
          ) : null}
          <p className={`text-xs text-slate-500 ${embedded ? '' : 'mt-0.5'}`}>
            {loading ? 'Loading…' : `${totals.rows} receipt${totals.rows === 1 ? '' : 's'} · ${fmtINR(totals.amount)} in view`}
          </p>
        </div>
        <ExportButton filename="receipts" columns={exportColumns} rows={rows} testid="receipts-export" />
      </div>

      {/* Summary cards — daily cash-in is the headline KPI for collections. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 border border-emerald-200 bg-emerald-50/60 rounded-[10px] shadow-sm" data-testid="receipts-today-card">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
            <IndianRupee className="h-3.5 w-3.5" /> Today's cash-in
          </div>
          <div className="text-2xl font-bold text-emerald-800 mt-1 font-mono" data-testid="receipts-today-total">{fmtINR(totals.todayCashIn)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-[10px] shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <TrendingUp className="h-3.5 w-3.5" /> Total in view
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1 font-mono" data-testid="receipts-view-total">{fmtINR(totals.amount)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-[10px] shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            <ReceiptIcon className="h-3.5 w-3.5" /> Receipts in view
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1 font-mono">{totals.rows}</div>
        </Card>
      </div>

      <Card className="p-3 border border-slate-200 rounded-[10px] shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</label>
            <Input
              type="date"
              value={filters.start}
              onChange={(e) => setFilters({ ...filters, start: e.target.value })}
              data-testid="receipts-filter-start"
              className="h-8 text-xs"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</label>
            <Input
              type="date"
              value={filters.end}
              onChange={(e) => setFilters({ ...filters, end: e.target.value })}
              data-testid="receipts-filter-end"
              className="h-8 text-xs"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Method</label>
            <Select value={filters.method || 'all'} onValueChange={(v) => setFilters({ ...filters, method: v === 'all' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="receipts-filter-method">
                <SelectValue placeholder="Any method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any method</SelectItem>
                {METHODS.filter(Boolean).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trip ID</label>
            <Input
              value={filters.trip_id}
              onChange={(e) => setFilters({ ...filters, trip_id: e.target.value })}
              placeholder="TRP_…"
              data-testid="receipts-filter-tripid"
              className="h-8 text-xs"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Traveller</label>
            <Input
              value={filters.traveller}
              onChange={(e) => setFilters({ ...filters, traveller: e.target.value })}
              placeholder="Name or UCC"
              data-testid="receipts-filter-traveller"
              className="h-8 text-xs"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={onApplyFilters}
              data-testid="receipts-apply-btn"
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white w-full h-8 text-xs"
            >
              <Search className="h-3 w-3 mr-1" /> Apply
            </Button>
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 rounded-[10px] shadow-sm overflow-hidden" data-testid="receipts-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="receipts-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trip ID</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Traveller</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Amount</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Method</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">UTR / Ref</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Type</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Balance after</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">No receipts match the current filters.</td></tr>
              ) : (
                rows.map((r) => <Row key={r.receiptId || `${r.tripId}-${r.date}-${r.amount}`} r={r} />)
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between text-xs text-slate-500" data-testid="receipts-pager">
        <span>Page {page + 1} · {rows.length} on this page</span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            data-testid="receipts-prev-btn"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={rows.length < PAGE_SIZE}
            onClick={() => setPage((p) => p + 1)}
            data-testid="receipts-next-btn"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ r }) => {
  const typeColor = r.type === 'Full' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700';
  const tripLabel = r.tripDisplayId || r.invoiceNumber || '—';
  return (
    <tr data-testid={`receipt-row-${r.receiptId || r.tripDisplayId}`} className="hover:bg-slate-50">
      <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap">{fmtDate(r.date)}</td>
      <td className="px-3 py-2">
        {r.tripDisplayId ? (
          <Link to={`/quotes/${encodeURIComponent(r.tripDisplayId)}/edit`} className="font-mono text-teal-700 hover:underline">{tripLabel}</Link>
        ) : r.invoiceNumber ? (
          <Link to={`/invoice-preview/${encodeURIComponent(r.invoiceNumber)}`} className="font-mono text-teal-700 hover:underline">{tripLabel}</Link>
        ) : (
          <span className="font-mono text-slate-600">{tripLabel}</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-slate-800">{r.traveller || '—'}</div>
        {r.clientUCC && <div className="text-[10px] font-mono text-slate-400">{r.clientUCC}</div>}
      </td>
      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">
        {fmtINR(r.grossAmount || r.amount)}
        {r.gatewayFee > 0 && (
          <div className="text-[10px] font-normal text-right" data-testid={`receipt-gateway-breakdown-${r.receiptId || r.tripDisplayId}`}>
            <span className="text-rose-500">Fee: {fmtINR(r.gatewayFee)}</span>{' · '}
            <span className="text-slate-400">Net: {fmtINR(r.netAmount)}</span>
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-slate-700">
        {r.method || '—'}
        {r.gateway && <span className="block text-[10px] text-slate-400 capitalize">via {r.gateway}</span>}
      </td>
      <td className="px-3 py-2 font-mono text-slate-500">{r.bankRef || '—'}</td>
      <td className="px-3 py-2">
        <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${typeColor}`}>{r.type}</span>
      </td>
      <td className="px-3 py-2 text-right font-mono text-slate-700">{fmtBalance(r.balanceAfter)}</td>
    </tr>
  );
};

export default FinanceReceipts;
